/* eslint-disable no-unused-vars */
/* eslint-disable max-len */
/* eslint-disable react/forbid-prop-types */
/* eslint-disable react/jsx-props-no-spreading */
import {
  Box, Grid, makeStyles, Table,
  TableBody, TableContainer, TableFooter, TablePagination, TableRow,
  LinearProgress, Paper, TableHead, Card, CardContent, Typography,
} from '@material-ui/core';
import { Auth } from 'aws-amplify';
import PropTypes from 'prop-types';
import {
  React,
} from 'react';
import { useMutation } from '@tanstack/react-query';
import useGuardedExport from '../../../hooks/useGuardedExport';
import GraphqlService from '../../../service/graphqlService';
import TablePaginationActions from '../../../components/utils/TablePaginationActions';
import SecondaryButton from '../../../components/utils/SecondaryButton';
import { useSnackbar } from '../../../providers/SnackbarContext';
import { useProject } from '../../../config/ProjectContext';
import useAnalytics from '../../../hooks/useAnalytics';
import { StyledTableCell, StyledTableRow } from '../../../components/utils/StyledTable';
import getCurrentAuthenticatedUserId from '../../../utils/getCurrentAuthenticatedUserId';

const useStyles = makeStyles((theme) => ({

  bottomRightExportButton: {
    paddingRight: 8,
    paddingLeft: 8,
    paddingBottom: 16,
    textAlign: 'right',
    alignSelf: 'flex-end',
    borderRadius: 0,
    width: 138,
  },
  historyTable: {
    borderRadius: 0,
    '& tr': {
      height: 48,
      '& td': {
        paddingTop: 0,
        paddingBottom: 0,
        paddingLeft: 20,
        paddingRight: 12,
        height: 48,
        '&.link-cell': {
          cursor: 'pointer',
          color: theme.palette.primary.main,
          textDecoration: 'none !important',
          '&:hover': {
            cursor: 'pointer',
            color: theme.palette.primary.dark,
          },
          '& a': {
            color: 'inherit',
            textDecoration: 'none',
          },
        },
      },
    },
  },
  separateVoidGrid: {
    flexWrap: 'noWrap',
    borderRadius: '12px !important',
  },
  content: {
    paddingTop: 16,
    paddingBottom: 16,
    paddingLeft: 24,
    paddingRight: 24,
    backgroundColor: theme.palette.background.default,
  },
  exportCSVText: {
    fontSize: 12,
    fontWeight: theme.typography.fontWeightRegular,
    textTransform: 'none',
  },
  contentGrid: {
    paddingBottom: 16,
  },
  cardNoMachineTelemetryText: {
    color: theme.palette.primary.text,
    fontWeight: theme.typography.fontWeightMedium,
    fontSize: 16,
    textAlign: 'center',
  },
  detailsTitle: {
    fontSize: 14,
    fontWeight: theme.typography.fontWeightMedium,
    paddingRight: '5px',
  },
  headingTitle: {
    fontSize: 16,
    fontWeight: theme.typography.fontWeightMedium,
  },
  machineInfoCardPadding16: {
    padding: '16px !important',
  },
  telemetryTable: {
    borderRadius: 12,
    boxShadow: theme.customShadows.noTopShadow,
  },
  bottomLeftLegend: {
    paddingTop: 4,
  },
  cardNoMachineTelemetry: {
    borderRadius: 12,
  },
  footerGrid: {
    width: 9,
  },
  toolbar: {
    width: 350,
  },
}));

const updatedHeaderExport = [
  'Device Timestamp (Local)',
  'Device Timestamp (UTC)',
  'Device Serial',
  'Module Serial',
  'Market',
  'Recipe',
  'Recipe Extra',
  'Product',
  'Extra',
  'Platform Timestamp (UTC)',
];
const transformDataList = (dataList) => dataList?.map((item) => ({
  'Device Timestamp (Local)': item.createdOnLocal,
  'Device Timestamp (UTC)': item.createdOnUtc,
  'Device Serial': item.deviceSerial,
  'Module Serial': item.moduleSerial,
  Market: item.countryCode || '-',
  Recipe: `${item.dictRecipe?.code || ''} - ${item.dictRecipe?.name || ''}`,
  'Recipe Extra': item.dictRecipe?.extra || '',
  Product: `${item.dictProduct?.code || ''} - ${item.dictProduct?.name || ''}`,
  Extra: item.extra,
  'Platform Timestamp (UTC)': item.processedOnUtc,
}));

export default function BrandConsumptionsTable({
  search, dataList, totalCount, filters, loading, dateIni, dateEnd,
  page, setPage, rowsPerPage, showDeviceSerial = true, id
}) {
  const { project } = useProject();
  const { setAnalytics } = useAnalytics();
  const transformedDataList = transformDataList(dataList);
  const openSnackBar = useSnackbar();

  const createNotificationMutation = useMutation((variables) => GraphqlService.createNotification(variables.email, variables.notificationTitle, variables.notificationDescription));

  // Determinar si es overview o no
  const isOverview = id === 'overview';

  async function runExport() {
    setAnalytics({
      event: 'button_clicked',
      extraParams: {
        name: isOverview
          ? 'device_management-overview-consumptions-export_csv'
          : 'device_management-consumptions-export_csv',
      },
    });
    try {
      const email = await getCurrentAuthenticatedUserId()

      await createNotificationMutation.mutateAsync({ email, notificationTitle: 'CSV file is being generated', notificationDescription: 'Your CSV file is being generated. You will receive a notification with the download link shortly.' });
      await GraphqlService.getBrandConsumptionsCSV(
        project.code, filters, dateIni, dateEnd,
      );
      openSnackBar('CSV file is being generated', 'info');
    } catch (error) {
      openSnackBar(error.message, 'error');
    }
  }

  const { requestExport, modal: exportModal } = useGuardedExport(
    {
      action: 'brandConsumptions',
      projectId: project?.code,
      dateIni,
      dateEnd,
      filters: typeof filters === 'string' ? filters : JSON.stringify(filters || {}),
      totalRows: totalCount,
      title: 'Export brand consumptions CSV',
    },
    runExport,
  );

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    search(newPage);
  };

  const handleDeviceSerialClick = () => {
    setAnalytics({
      event: 'button_clicked',
      extraParams: {
        name: isOverview
          ? 'device_management-overview-consumptions-device_serial_clicked'
          : 'device_management-consumptions-device_serial_clicked',
      },
    });
  };

  const classes = useStyles();

  const filteredHeaderExport = updatedHeaderExport.filter((header) => {
    if (header === 'Device Serial') return showDeviceSerial;
    if (header === 'Module Serial') return dataList?.some((item) => item.moduleSerial);
    return true;
  });

  return (
    <Box>
      <Grid
        container
        spacing={0}
        direction="row"
        className={classes.content}
        id="brand-consumptions-table"
      >
        {(transformedDataList !== null) && (transformedDataList?.length > 0)
          && (
            <Grid
              container
              spacing={0}
              direction="row"
              className={classes.contentGrid}
            >
              <Grid item xs={6} className={classes.bottomLeftLegend}>
                <Typography className={classes.bottomLeftLegend}>Data will be retrieved in real time</Typography>
              </Grid>

              <Grid item xs={6} className={classes.bottomRightExportButton}>
                <span>
                  <SecondaryButton
                    id="btnConsumptionsExport"
                    onClick={() => requestExport()}
                  >
                    Export .CSV
                  </SecondaryButton>
                  {exportModal}
                </span>
              </Grid>

              <Grid
                item
                xs={12}
                className={classes.separate}
              >
                <TableContainer component={Paper} className={classes.telemetryTable}>
                  <Table className={classes.historyTable} aria-label="custom pagination table">
                    <TableHead>
                      <TableRow>
                        {filteredHeaderExport?.map((head) => <StyledTableCell key={head}>{head}</StyledTableCell>)}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {transformedDataList?.map((item) => (
                        <StyledTableRow key={`${item['Device Serial']}-${item['Device Timestamp (UTC)']}`}>
                          {filteredHeaderExport?.map((header) => {
                            const cellKey = `${item['Device Serial']}-${item['Device Timestamp (UTC)']}-${header}`;
                            const serial = item['Device Serial'];
                            if (header === 'Device Serial' && serial) {
                              return (
                                <StyledTableCell key={cellKey} className="link-cell">
                                  {/* Hash prefix is required: the app is mounted on a HashRouter */}
                                  <a
                                    href={`#/statistics/deviceOverview/${project?.code}/${encodeURIComponent(serial)}`}
                                    onClick={handleDeviceSerialClick}
                                  >
                                    {serial}
                                  </a>
                                </StyledTableCell>
                              );
                            }
                            return <StyledTableCell key={cellKey}>{item[header]}</StyledTableCell>;
                          })}
                        </StyledTableRow>
                      ))}
                    </TableBody>
                    <Grid className={classes.footerGrid}>
                      <TableFooter>
                        <TableRow key="paginationFooter">
                          <TablePagination
                            rowsPerPageOptions={[20]}
                            colSpan={10}
                            count={totalCount}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            classes={{ toolbar: classes.toolbar }}
                            SelectProps={{
                              inputProps: { 'aria-label': 'rows per page' },
                              native: true,
                            }}
                            onChangePage={handleChangePage}
                            ActionsComponent={(props) => (
                              <TablePaginationActions
                                {...props}
                                onChangePage={handleChangePage}
                              />
                            )}
                          />
                        </TableRow>
                      </TableFooter>
                    </Grid>
                  </Table>
                  {loading && <LinearProgress />}
                </TableContainer>
              </Grid>
            </Grid>
          )}
        {(transformedDataList !== null) && (transformedDataList?.length === 0)
          && !loading
          && (
            <Grid
              item
              xs={12}
              className={classes.separateVoidGrid}
            >
              <Card className={classes.cardNoMachineTelemetry}>
                <CardContent className={classes.machineInfoCardPadding16}>
                  <Typography className={classes.cardNoMachineTelemetryText}>
                    No device consumptions available
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          )}
      </Grid>
    </Box>
  );
}
BrandConsumptionsTable.propTypes = {
  search: PropTypes.func.isRequired,
  dataList: PropTypes.array.isRequired,
  totalCount: PropTypes.number.isRequired,
  filters: PropTypes.string.isRequired,
  loading: PropTypes.bool.isRequired,
  dateIni: PropTypes.object.isRequired,
  dateEnd: PropTypes.object.isRequired,
  page: PropTypes.number.isRequired,
  setPage: PropTypes.func.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
  showDeviceSerial: PropTypes.bool,
};

BrandConsumptionsTable.defaultProps = {
  showDeviceSerial: true,
};
